import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { trackFormView, trackFormSubmit } from "@/lib/analytics";
import { useAuth } from "@/context/AuthContext";
import { useSeo } from "@/hooks/useSeo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ImagePlus, X, Loader2, ArrowLeft, Eye, Send, Star, ShieldCheck } from "lucide-react";

const CONDITIONS = [
  { v: "neuf", l: "Neuf" },
  { v: "tres-bon", l: "Très bon état" },
  { v: "bon", l: "Bon état" },
  { v: "correct", l: "État correct" },
  { v: "pieces", l: "Pour pièces / à réparer" },
];
const MAX_IMAGES = 5;

export default function AnnonceForm() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const { user } = useAuth();
  const isGuest = !isEdit && !(user && user !== false);
  const fileRef = useRef(null);
  const [cats, setCats] = useState([]);
  const [communes, setCommunes] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(false);
  const [accept, setAccept] = useState(false);
  const [form, setForm] = useState({
    title: "", description: "", category_slug: "", price_type: "fixed", price: "",
    condition: "bon", city: "", brand: "", model: "", hand_delivery: true,
    extra_info: "", contact_phone: "", show_phone: false, images: [], main_image: null,
    name: "", email: "",
  });

  useSeo({ title: isEdit ? "Modifier mon annonce | PROXIreduc-28" : "Déposer une annonce | PROXIreduc-28", canonicalPath: "/petites-annonces/deposer" });

  useEffect(() => { if (!isEdit) trackFormView("annonce"); }, [isEdit]);
  useEffect(() => {
    api.get("/annonces/categories").then((r) => setCats((r.data || []).filter((c) => !c.restricted))).catch(() => {});
    api.get("/annonces/communes").then((r) => setCommunes(r.data?.suggestions || [])).catch(() => {});
    if (isEdit) {
      api.get(`/me/annonces/${id}`).then((r) => {
        const a = r.data;
        setForm({
          title: a.title || "", description: a.description || "", category_slug: a.category_slug || "",
          price_type: a.price_type || "fixed", price: a.price ?? "", condition: a.condition || "bon",
          city: a.city || "", brand: a.brand || "", model: a.model || "", hand_delivery: !!a.hand_delivery,
          extra_info: a.extra_info || "", contact_phone: a.contact_phone || "", show_phone: !!a.show_phone,
          images: a.images || [], main_image: a.main_image || null,
        });
        setAccept(true);
      }).catch(() => { toast.error("Annonce introuvable"); navigate("/compte/mes-annonces"); });
    }
  }, [id, isEdit, navigate]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (isEdit) return;
    try {
      const raw = localStorage.getItem("annonce_ai_draft");
      if (raw) {
        const d = JSON.parse(raw);
        setForm((f) => ({
          ...f,
          title: d.title || f.title,
          description: d.description || f.description,
          city: d.city || f.city,
          price: (d.price !== null && d.price !== undefined && d.price !== "") ? String(d.price) : f.price,
          price_type: (d.price !== null && d.price !== undefined && d.price !== "") ? "fixed" : f.price_type,
        }));
        localStorage.removeItem("annonce_ai_draft");
        toast.success("Texte de l'IA pré-rempli — complétez les détails puis publiez.");
      }
    } catch { /* ignore */ }
  }, [isEdit]);

  const onPickImages = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    const room = MAX_IMAGES - form.images.length;
    if (room <= 0) { toast.error(`${MAX_IMAGES} photos maximum`); return; }
    if (files.length > room) toast.message(`Seules ${room} photo(s) seront ajoutées (${MAX_IMAGES} maximum).`);
    setUploading(true);
    try {
      const urls = [];
      for (const file of files.slice(0, room)) {
        const fd = new FormData(); fd.append("file", file);
        const { data } = await api.post("/annonces/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
        if (data?.url) urls.push(data.url);
      }
      setForm((f) => {
        const imgs = [...f.images, ...urls].slice(0, MAX_IMAGES);
        return { ...f, images: imgs, main_image: f.main_image || imgs[0] || null };
      });
      if (urls.length) toast.success("Photo(s) ajoutée(s)");
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload impossible"); }
    finally { setUploading(false); }
  };

  const removeImage = (url) => setForm((f) => {
    const imgs = f.images.filter((u) => u !== url);
    return { ...f, images: imgs, main_image: f.main_image === url ? (imgs[0] || null) : f.main_image };
  });

  const validate = () => {
    if (form.title.trim().length < 5) return "Le titre doit faire au moins 5 caractères.";
    if (form.description.trim().length < 15) return "La description doit faire au moins 15 caractères.";
    if (!form.category_slug) return "Choisissez une catégorie.";
    if (!form.city.trim()) return "Indiquez la commune.";
    if (form.price_type !== "free" && (!form.price || Number(form.price) <= 0)) return "Indiquez un prix, ou choisissez « Gratuit ».";
    if (isGuest && (!form.email.trim() || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim()))) return "Ajoutez votre e-mail pour publier et gérer votre annonce.";
    if (!accept) return "Vous devez accepter les règles de publication.";
    return null;
  };

  const submit = async () => {
    const err = validate();
    if (err) { toast.error(err); return; }
    setSaving(true);
    const payload = {
      ...form,
      price: form.price_type === "free" ? null : Number(form.price),
    };
    try {
      if (isEdit) {
        const { data } = await api.put(`/annonces/${id}`, payload);
        toast.success("Annonce mise à jour ✅");
        navigate(`/petites-annonces/annonce/${data.slug}`);
      } else {
        const { data } = await api.post("/annonces", payload);
        trackFormSubmit("annonce");
        toast.success(data.account_created
          ? "Votre annonce est publiée ✅ Un espace membre gratuit a été créé — vos identifiants vous ont été envoyés par e-mail."
          : "Votre annonce est publiée ✅");
        navigate(`/petites-annonces/annonce/${data.slug}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Publication impossible");
    } finally { setSaving(false); }
  };

  const priceLabel = form.price_type === "free" ? "Gratuit"
    : form.price ? `${form.price} €${form.price_type === "negotiable" ? " (négociable)" : ""}` : "Prix non précisé";

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-28" data-testid="annonce-form-page">
      <Link to={isEdit ? "/compte/mes-annonces" : "/petites-annonces"} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-[#E05638]"><ArrowLeft size={14} /> Retour</Link>
      <h1 className="mt-3 font-heading text-2xl sm:text-3xl font-extrabold text-slate-900">{isEdit ? "Modifier mon annonce" : "Déposer une annonce"}</h1>
      <p className="mt-1 text-sm text-slate-500">Gratuit pour les particuliers. Remplissez simplement les champs ci-dessous.</p>

      <div className="mt-6 bg-white border border-slate-200 rounded-2xl p-5 space-y-5">
        <div>
          <label className="text-sm font-medium text-slate-800">Titre de l'annonce *</label>
          <Input value={form.title} onChange={(e) => set("title", e.target.value)} maxLength={90} placeholder="Ex. Vélo enfant 20 pouces très bon état" data-testid="form-title" />
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium text-slate-800">Catégorie *</label>
            <Select value={form.category_slug} onValueChange={(v) => set("category_slug", v)}>
              <SelectTrigger data-testid="form-category"><SelectValue placeholder="Choisir…" /></SelectTrigger>
              <SelectContent>{cats.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-sm font-medium text-slate-800">État du bien *</label>
            <Select value={form.condition} onValueChange={(v) => set("condition", v)}>
              <SelectTrigger data-testid="form-condition"><SelectValue /></SelectTrigger>
              <SelectContent>{CONDITIONS.map((c) => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>

        <div>
          <label className="text-sm font-medium text-slate-800">Description *</label>
          <Textarea rows={5} value={form.description} onChange={(e) => set("description", e.target.value)} maxLength={5000}
                    placeholder="Décrivez l'objet, son état, ses dimensions, la raison de la vente…" data-testid="form-description" />
          <div className="text-xs text-slate-400 text-right">{form.description.length}/5000</div>
        </div>

        {/* Prix */}
        <div>
          <label className="text-sm font-medium text-slate-800">Prix *</label>
          <div className="flex flex-wrap gap-2 mt-1">
            {[{ v: "fixed", l: "Prix fixe" }, { v: "negotiable", l: "Négociable" }, { v: "free", l: "Gratuit (don)" }].map((o) => (
              <button key={o.v} type="button" onClick={() => set("price_type", o.v)} data-testid={`form-pricetype-${o.v}`}
                      className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${form.price_type === o.v ? "bg-[#E05638] text-white border-[#E05638]" : "bg-white text-slate-700 border-slate-300 hover:border-slate-400"}`}>
                {o.l}
              </button>
            ))}
          </div>
          {form.price_type !== "free" && (
            <div className="relative mt-2 max-w-[180px]">
              <Input type="number" min="0" value={form.price} onChange={(e) => set("price", e.target.value)} placeholder="0" className="pr-8" data-testid="form-price" />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">€</span>
            </div>
          )}
        </div>

        {/* Commune */}
        <div>
          <label className="text-sm font-medium text-slate-800">Commune *</label>
          <Input list="communes-list" value={form.city} onChange={(e) => set("city", e.target.value)} placeholder="Ex. Chartres" data-testid="form-city" />
          <datalist id="communes-list">{communes.map((c) => <option key={c} value={c} />)}</datalist>
        </div>

        {/* Photos */}
        <div>
          <label className="text-sm font-medium text-slate-800">Photos (jusqu'à {MAX_IMAGES})</label>
          <p className="text-xs text-slate-400 mb-2">Les données de localisation (GPS) sont automatiquement retirées. Cliquez une photo pour la définir comme principale.</p>
          <div className="flex flex-wrap gap-3">
            {form.images.map((url) => (
              <div key={url} className={`relative w-24 h-24 rounded-xl overflow-hidden border-2 group ${form.main_image === url ? "border-[#E05638]" : "border-slate-200"}`} data-testid="form-image">
                <img src={url} alt="" className="w-full h-full object-cover cursor-pointer" onClick={() => set("main_image", url)} />
                {form.main_image === url && <span className="absolute bottom-1 left-1 bg-[#E05638] text-white text-[9px] font-bold px-1.5 py-0.5 rounded inline-flex items-center gap-0.5"><Star size={8} /> Principale</span>}
                <button type="button" onClick={() => removeImage(url)} className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/60 text-white grid place-items-center opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity" data-testid="form-image-remove"><X size={13} /></button>
              </div>
            ))}
            {form.images.length < MAX_IMAGES && (
              <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="w-24 h-24 rounded-xl border-2 border-dashed border-slate-300 text-slate-400 hover:border-[#E05638] hover:text-[#E05638] grid place-items-center transition-colors" data-testid="form-image-add">
                {uploading ? <Loader2 size={20} className="animate-spin" /> : <ImagePlus size={22} />}
              </button>
            )}
          </div>
          <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={onPickImages} data-testid="form-image-input" />
        </div>

        {/* Champs complémentaires */}
        <details className="group">
          <summary className="cursor-pointer text-sm font-medium text-slate-700 select-none">Informations complémentaires (facultatif)</summary>
          <div className="mt-3 space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div><label className="text-sm text-slate-600">Marque</label><Input value={form.brand} onChange={(e) => set("brand", e.target.value)} data-testid="form-brand" /></div>
              <div><label className="text-sm text-slate-600">Modèle</label><Input value={form.model} onChange={(e) => set("model", e.target.value)} data-testid="form-model" /></div>
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
              <input type="checkbox" checked={form.hand_delivery} onChange={(e) => set("hand_delivery", e.target.checked)} data-testid="form-hand" /> Remise en main propre possible
            </label>
            <div>
              <label className="text-sm text-slate-600">Téléphone de contact (facultatif)</label>
              <Input value={form.contact_phone} onChange={(e) => set("contact_phone", e.target.value)} placeholder="06 12 34 56 78" data-testid="form-phone" />
              <label className="flex items-center gap-2 text-xs text-slate-500 mt-1 cursor-pointer">
                <input type="checkbox" checked={form.show_phone} onChange={(e) => set("show_phone", e.target.checked)} data-testid="form-showphone" /> Afficher mon téléphone publiquement sur l'annonce
              </label>
            </div>
          </div>
        </details>

        {/* Visiteur non connecté : e-mail discret pour créer/retrouver son espace */}
        {isGuest && (
          <div className="grid sm:grid-cols-2 gap-4" data-testid="annonce-anon-fields">
            <div>
              <label className="text-sm font-medium text-slate-800">Prénom <span className="text-slate-400 font-normal">(facultatif)</span></label>
              <Input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Votre prénom" data-testid="annonce-anon-name" />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-800">Votre e-mail</label>
              <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="vous@email.fr" data-testid="annonce-anon-email" />
            </div>
            <p className="sm:col-span-2 -mt-1 text-[11px] text-slate-400">Pour recevoir les réponses des acheteurs et gérer votre annonce. Un espace membre gratuit sera créé pour recevoir et suivre les réponses — accès envoyé par e-mail.</p>
          </div>
        )}

        {/* Règles */}
        <label className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer bg-slate-50 rounded-xl p-3">
          <input type="checkbox" checked={accept} onChange={(e) => setAccept(e.target.checked)} className="mt-0.5" data-testid="form-accept" />
          <span>J'ai lu et j'accepte les <Link to="/petites-annonces/regles" target="_blank" className="text-[#E05638] font-semibold underline">règles de publication</Link>. Mon annonce est conforme et pourra être modérée.</span>
        </label>
        <div className="text-xs text-slate-400 flex items-center gap-1"><ShieldCheck size={13} /> Publication immédiate, puis vérifiée par notre équipe de modération.</div>

        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setPreview((v) => !v)} data-testid="form-preview-toggle"><Eye size={15} className="mr-1.5" /> Aperçu</Button>
          <Button onClick={submit} disabled={saving} className="btn-shine text-white flex-1" data-testid="form-submit">
            {saving ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Send size={16} className="mr-2" />}
            {isEdit ? "Enregistrer les modifications" : "Publier mon annonce"}
          </Button>
        </div>

        {preview && (
          <div className="mt-2 border border-slate-200 rounded-2xl overflow-hidden" data-testid="form-preview">
            <div className="aspect-[2/1] bg-slate-100">
              {form.main_image ? <img src={form.main_image} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full grid place-items-center text-slate-300">Aucune photo</div>}
            </div>
            <div className="p-4">
              <div className="font-semibold text-slate-900">{form.title || "Titre de l'annonce"}</div>
              <div className="font-heading font-extrabold text-[#E05638] mt-1">{priceLabel}</div>
              <div className="text-xs text-slate-500 mt-1">{form.city || "Commune"}</div>
              <p className="mt-2 text-sm text-slate-600 whitespace-pre-line line-clamp-4">{form.description}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
